import React, { useEffect, useState } from "react";
import { messageBarApi } from "../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Loader2, Pencil, X } from "lucide-react";

const BG = { blue: "#1e3a8a", green: "#065f46", amber: "#b45309", red: "#b91c1c", gray: "#374151" };
const PRIORITY_BG = { information: "blue", success: "green", reminder: "amber", maintenance: "gray", important: "amber", critical: "red" };
const AUDIENCE_LABELS = {
  everyone: "Everyone", public_visitors: "Public Landing Visitors", school_users: "School Users",
  portfolio_managers: "Portfolio Managers", employees: "Employees", sales_users: "Sales Users",
  pilot_participants: "Pilot Participants", admin_preview: "Super Admin Preview Only",
};
const PRIORITIES = ["information", "success", "reminder", "maintenance", "important", "critical"];
const COLORS = ["", "blue", "green", "amber", "red", "gray"];

const EMPTY = {
  enabled: true, message: "", audiences: ["everyone"], priority: "information",
  bg_color: "", text_color: "white", scroll_speed: 90, start_date: "", end_date: "",
  timezone: "UTC", pause_on_hover: true, closeable: true, show_again_after_login: false,
  link_url: "", link_label: "", link_target: "_self",
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">{label}</label>
    {children}
  </div>
);

function Preview({ form }) {
  const key = form.bg_color || PRIORITY_BG[form.priority] || "blue";
  return (
    <div className="rounded-lg overflow-hidden border border-border" data-testid="mb-preview">
      <div className="w-full flex items-center px-4" style={{ background: BG[key] || BG.blue, color: form.text_color || "#fff", height: 44 }}>
        <span className="text-sm font-medium truncate">{form.message || "Your announcement preview…"}</span>
      </div>
    </div>
  );
}

function statusOf(a) {
  if (!a.enabled) return { t: "Disabled", c: "bg-muted text-foreground/50" };
  if (!a.is_active_now) return { t: "Scheduled / Expired", c: "bg-amber-100 text-amber-800" };
  return { t: "Live", c: "bg-emerald-100 text-emerald-700" };
}

export default function MessageBarSettings({ session }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = list view; object = editing
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    messageBarApi.list(session)
      .then((d) => setItems(d.announcements || []))
      .catch(() => toast.error("Failed to load announcements"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [session]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAud = (a) => setForm((f) => {
    const has = f.audiences.includes(a);
    return { ...f, audiences: has ? f.audiences.filter((x) => x !== a) : [...f.audiences, a] };
  });

  const save = async () => {
    if (!form.message.trim()) { toast.error("Message text is required"); return; }
    if (!form.audiences.length) { toast.error("Select at least one audience"); return; }
    setSaving(true);
    try {
      if (form.id) await messageBarApi.update(form.id, form, session);
      else await messageBarApi.create(form, session);
      toast.success("Announcement saved");
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await messageBarApi.remove(id, session); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-foreground/50 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  // ---- Editor ----
  if (form) {
    return (
      <div className="space-y-5" data-testid="mb-editor">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-primary" /> {form.id ? "Edit" : "New"} announcement
          </h3>
          <Button size="sm" variant="ghost" onClick={() => setForm(null)} data-testid="mb-cancel"><X className="h-4 w-4 mr-1" /> Cancel</Button>
        </div>

        <Preview form={form} />

        <Field label="Message text">
          <Textarea data-testid="mb-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={2} placeholder="e.g. Planned maintenance Sunday 2–4pm ET." />
        </Field>

        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">Audience</label>
          <div className="flex flex-wrap gap-2" data-testid="mb-audiences">
            {Object.keys(AUDIENCE_LABELS).map((a) => (
              <button key={a} type="button" onClick={() => toggleAud(a)} data-testid={`mb-aud-${a}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.audiences.includes(a) ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/60 hover:border-primary/40"}`}>
                {AUDIENCE_LABELS[a]}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Priority">
            <select data-testid="mb-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm capitalize">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Background color">
            <select data-testid="mb-bg" value={form.bg_color} onChange={(e) => set("bg_color", e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm capitalize">
              {COLORS.map((c) => <option key={c} value={c}>{c || "Auto (by priority)"}</option>)}
            </select>
          </Field>
          <Field label="Scroll speed (px/sec)">
            <Input data-testid="mb-speed" type="number" value={form.scroll_speed} onChange={(e) => set("scroll_speed", Number(e.target.value))} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Start date/time (optional)">
            <Input data-testid="mb-start" type="datetime-local" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          </Field>
          <Field label="End date/time (optional)">
            <Input data-testid="mb-end" type="datetime-local" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} />
          </Field>
          <Field label="Time zone">
            <Input data-testid="mb-tz" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} placeholder="UTC" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Link URL (optional)">
            <Input data-testid="mb-link-url" value={form.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="https://…" />
          </Field>
          <Field label="Link opens in">
            <select data-testid="mb-link-target" value={form.link_target} onChange={(e) => set("link_target", e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm">
              <option value="_self">Same tab</option>
              <option value="_blank">New tab</option>
            </select>
          </Field>
          <Field label="Text color">
            <Input data-testid="mb-text-color" value={form.text_color} onChange={(e) => set("text_color", e.target.value)} placeholder="white" />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-foreground/70"><input type="checkbox" data-testid="mb-enabled" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enabled</label>
          <label className="flex items-center gap-2 text-sm text-foreground/70"><input type="checkbox" data-testid="mb-pause" checked={form.pause_on_hover} onChange={(e) => set("pause_on_hover", e.target.checked)} /> Pause on hover</label>
          <label className="flex items-center gap-2 text-sm text-foreground/70"><input type="checkbox" data-testid="mb-closeable" checked={form.closeable} onChange={(e) => set("closeable", e.target.checked)} /> Closeable by user</label>
          <label className="flex items-center gap-2 text-sm text-foreground/70"><input type="checkbox" data-testid="mb-again" checked={form.show_again_after_login} onChange={(e) => set("show_again_after_login", e.target.checked)} /> Show again after login</label>
        </div>

        <Button onClick={save} disabled={saving} data-testid="mb-save" className="w-full sm:w-auto">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save announcement
        </Button>
      </div>
    );
  }

  // ---- List ----
  return (
    <div className="space-y-4" data-testid="message-bar-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-primary" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">Message Bar</h2>
            <p className="text-xs text-foreground/40">Publish scrolling announcements across customer-facing pages.</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setForm({ ...EMPTY })} data-testid="mb-new"><Plus className="h-4 w-4 mr-1.5" /> New announcement</Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-foreground/40">No announcements yet. Create one to display a message bar.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => {
            const st = statusOf(a);
            const key = a.bg_color || PRIORITY_BG[a.priority] || "blue";
            return (
              <div key={a.id} className="rounded-xl border border-border p-3 flex items-center gap-3" data-testid={`mb-item-${a.id}`}>
                <span className="h-8 w-1.5 rounded-full shrink-0" style={{ background: BG[key] }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{a.message}</div>
                  <div className="text-[11px] text-foreground/50 mt-0.5">
                    <span className="capitalize">{a.priority}</span> · {(a.audiences || []).map((x) => AUDIENCE_LABELS[x] || x).join(", ")}
                  </div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${st.c}`}>{st.t}</span>
                <Button size="sm" variant="ghost" onClick={() => setForm({ ...EMPTY, ...a })} data-testid={`mb-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(a.id)} data-testid={`mb-del-${a.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
